import { QueryClient, type QueryKey } from '@tanstack/react-query'

export type QuoteDetailReorderChange = {
  dsy_lineitemcpqfieldsid: string
  dsy_grouporder: number
}

export type QueryCacheSnapshot<T> = [QueryKey, T | undefined][]

export async function applyQuoteDetailReorderInChunks<T>(
  changes: T[],
  update: (change: T) => Promise<unknown>,
  chunkSize = 12
) {
  const results: unknown[] = []

  for (let i = 0; i < changes.length; i += chunkSize) {
    const sliceResults = await Promise.allSettled(changes.slice(i, i + chunkSize).map(update))
    const failure = sliceResults.find((result) => result.status === 'rejected')

    // Wait for every PATCH in the active chunk before rejecting. Otherwise a
    // sibling request can commit after the error handler refetches server truth.
    if (failure?.status === 'rejected') throw failure.reason

    results.push(
      ...sliceResults.map((result) => {
        if (result.status === 'rejected') throw result.reason
        return result.value
      })
    )
  }

  return results
}

export async function restoreAndRefetchQuoteDetailCaches<T>(
  queryClient: QueryClient,
  previous: QueryCacheSnapshot<T>
) {
  previous.forEach(([key, data]) => queryClient.setQueryData<T>(key, data))

  // Reordering uses multiple Dataverse PATCHes, so a later failure can leave
  // earlier chunks committed. Refetch every cached quote-detail view to replace
  // the optimistic rollback with the server's actual partial state.
  await queryClient.refetchQueries({ queryKey: ['quotedetails'], type: 'all' })
}
